/**
 * AI CONTEXT
 * Purpose: read/write hook for the shared artwork tag dictionary
 *   (`DOC_TYPES.ARTWORK_TAGS`) — the vocabulary behind the portfolio tag picker.
 * Reads: every ACTIVE tag doc, subscribed HERE rather than at app root.
 * Exposes: the tag list, a tagsById map, usage counts + picker ordering derived
 *   from the projects handed in, and createTag (dedupes case-insensitively).
 * Used by: ArtworkTagSelect callers (PortfolioPage, PortfolioDrillIn) and
 *   usePortfolioDbStorage (which needs tag TEXT for timeline messages).
 *
 * Modelled on `useStandoutMoments` (the `evaluationKeywords` dictionary). The
 * subscription lives in this hook and not in FirebaseContextProvider on
 * purpose: the app already over-subscribes globally (JARVIS-158) and only
 * portfolio surfaces need this collection.
 */
import { keyBy } from "lodash"
import { useCallback, useMemo } from "react"

import { artworkTagAtoms } from "#atoms"
import Firebase from "#classes/Firebase"
import { useUserContext } from "#contexts/UserContextProvider"
import { type ArtworkTagConstructor } from "#customTypes/composedTypes/artworkTagTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { invariant } from "#customTypes/types"
import { useFilteredDocs } from "#hooks/jotai/useSubscription"
import {
  type ArtworkTagged,
  type ArtworkTagOption,
  countArtworkTagUsage,
  findArtworkTagByText,
  normalizeArtworkTagText,
  orderArtworkTagsForPicker,
} from "#utils/artworkTagHelpers"

/** Frozen so the default argument is a stable dependency. */
const NO_PROJECTS: readonly ArtworkTagged[] = Object.freeze([])

/** Only active tags are offered; retiring a tag is `isActive: false`. */
const ACTIVE_TAGS_QUERY = {
  queries: {
    isActive: { value: true, comparator: COMPARATORS.EQUALS },
  },
} as const

export interface UseArtworkTagsResult {
  tags: ArtworkTagConstructor[]
  tagsById: Record<Uuid, ArtworkTagConstructor | undefined>
  /** Tag id -> how many of `projects` carry it. */
  usageCounts: Record<Uuid, number>
  /** Every active tag, most-used first then alphabetical. */
  orderedTags: ArtworkTagOption[]
  /** The subset with at least one use across `projects`. */
  usedTags: ArtworkTagOption[]
  /**
   * Returns the existing tag's id on a case-insensitive text match, otherwise
   * writes a new dictionary entry and returns its id. Null for empty text.
   */
  createTag: (text: string) => Promise<Uuid | null>
}

export const useArtworkTags = (
  projects: readonly ArtworkTagged[] = NO_PROJECTS
): UseArtworkTagsResult => {
  const { currentUserId } = useUserContext()

  const tags = useFilteredDocs<ArtworkTagConstructor>(
    artworkTagAtoms,
    DOC_TYPES.ARTWORK_TAGS,
    ACTIVE_TAGS_QUERY
  )

  const tagsById = useMemo(() => keyBy(tags, "id"), [tags])

  const usageCounts = useMemo(() => countArtworkTagUsage(projects), [projects])

  const orderedTags = useMemo(
    () => orderArtworkTagsForPicker(tags, usageCounts),
    [tags, usageCounts]
  )

  const usedTags = useMemo(
    () => orderedTags.filter((option) => option.usageCount > 0),
    [orderedTags]
  )

  const createTag = useCallback(
    async (text: string): Promise<Uuid | null> => {
      const normalized = normalizeArtworkTagText(text)
      if (!normalized) return null

      const existing = findArtworkTagByText(tags, normalized)
      if (existing) return existing.id

      // A dictionary entry attributed to nobody is a data defect, and there is
      // no path to this picker without a signed-in user, so refuse rather than
      // writing `createdBy: null`.
      invariant(
        currentUserId !== null,
        `Cannot create artwork tag "${normalized}" — _current_user_id is missing!`
      )

      const now = new Date().toISOString()
      const newTag: Omit<ArtworkTagConstructor, "id"> & { id?: Uuid } = {
        text: normalized,
        isSeeded: false,
        isActive: true,
        createdBy: currentUserId,
        createdAt: now,
        lastUpdatedAt: now,
        lastUpdatedBy: currentUserId,
      }

      return Firebase.getInstance().addDocument<ArtworkTagConstructor>(
        DOC_TYPES.ARTWORK_TAGS,
        newTag
      )
    },
    [currentUserId, tags]
  )

  return {
    tags,
    tagsById,
    usageCounts,
    orderedTags,
    usedTags,
    createTag,
  }
}

export default useArtworkTags
